/**
 * Secure File Viewer Helper
 * 
 * Generates protected viewer URLs for files (PDFs, images) 
 * that require authentication and have anti-screenshot/download protections.
 */

const VIEWER_BASE = '/secure-viewer';

/**
 * Encode a file URL to a safe base64 parameter
 * @param {string} url - The original file URL
 * @returns {string} Base64 encoded string
 */
const encodeFileUrl = (url) => {
  try {
    return btoa(encodeURIComponent(url));
  } catch (e) {
    console.error('Failed to encode file URL:', e);
    return '';
  }
};

/**
 * Decode a file URL from a base64 parameter
 * @param {string} encoded - Base64 encoded file URL
 * @returns {string|null} Decoded original URL or null on failure
 */
export const decodeFileUrl = (encoded) => {
  try {
    return decodeURIComponent(atob(encoded));
  } catch (e) {
    console.error('Failed to decode file URL:', e);
    return null;
  }
};

/**
 * Generate a secure viewer URL for a file
 * @param {string} fileUrl - The original file URL
 * @param {'pdf'|'image'} type - File type
 * @returns {string} The secure viewer URL
 */
export const getSecureFileUrl = (fileUrl, type = 'pdf') => {
  if (!fileUrl) return '#';
  const encoded = encodeFileUrl(fileUrl);
  if (!encoded) return fileUrl;
  return `${VIEWER_BASE}?type=${type}&file=${encoded}`;
};

/**
 * Parse secure viewer URL parameters
 * @param {URLSearchParams} searchParams - URL search params
 * @returns {{ type: string, fileUrl: string|null }}
 */
export const parseSecureViewerParams = (searchParams) => {
  const type = searchParams.get('type') || 'pdf';
  const encodedFile = searchParams.get('file');
  const fileUrl = encodedFile ? decodeFileUrl(encodedFile) : null;
  return { type, fileUrl };
};

export default {
  getSecureFileUrl,
  parseSecureViewerParams,
  decodeFileUrl,
};
